import React, { useEffect, useMemo } from 'react';
import { Box, Tooltip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ViewModule as ViewModuleIcon,
  ViewComfy as ViewComfyIcon,
  GridView as GridViewIcon,
} from '@mui/icons-material';
import {
  ProjectCardSize,
  DEFAULT_PROJECT_CARD_SIZE,
  PROJECT_CARD_SIZE_PREFERENCE_ORDER,
} from '../constants/cardSizes';
import { useModeTokens } from '@/common/components/ui/useModeTokens';

interface CardSizeToggleProps {
  cardSize: ProjectCardSize;
  setCardSize: (size: ProjectCardSize) => void;
  containerWidth: number;
}

const GRID_GAP_PX = 16;

const CARD_SIZE_OPTIONS = [
  { value: ProjectCardSize.SMALL, label: 'Small', Icon: ViewComfyIcon },
  { value: ProjectCardSize.MEDIUM, label: 'Medium', Icon: GridViewIcon },
  { value: ProjectCardSize.LARGE, label: 'Large', Icon: ViewModuleIcon },
] as const;

const CARD_SIZE_OPTIONS_BY_PREFERENCE = PROJECT_CARD_SIZE_PREFERENCE_ORDER.map(
  (preferredSize) =>
    CARD_SIZE_OPTIONS.find((option) => option.value === preferredSize)
).filter((option): option is (typeof CARD_SIZE_OPTIONS)[number] =>
  Boolean(option)
);

// Mirrors CSS grid packing for `minmax(cardSize, 1fr)` with a fixed gap.
// We use this to detect when two toggle options would render the same layout.
const getColumnCount = (containerWidth: number, minCardWidth: number): number =>
  Math.max(
    1,
    Math.floor((containerWidth + GRID_GAP_PX) / (minCardWidth + GRID_GAP_PX))
  );

const CardSizeToggle: React.FC<CardSizeToggleProps> = ({
  cardSize,
  setCardSize,
  containerWidth,
}) => {
  const t = useModeTokens();
  const availableCardSizes = useMemo<ProjectCardSize[]>(() => {
    if (!containerWidth) return CARD_SIZE_OPTIONS.map((option) => option.value);

    // Keep one option per unique resulting column count to avoid no-op toggles
    // (for example, when 300 and 400 both produce a 1-column layout).
    // Iterate in preference order so when multiple sizes map to the same
    // column count, Medium is preserved before Small/Large.
    const byColumnCount = new Map<number, ProjectCardSize>();
    CARD_SIZE_OPTIONS_BY_PREFERENCE.forEach((option) => {
      const columns = getColumnCount(containerWidth, option.value);
      if (!byColumnCount.has(columns)) {
        byColumnCount.set(columns, option.value);
      }
    });

    return Array.from(byColumnCount.values());
  }, [containerWidth]);

  useEffect(() => {
    // If the viewport changes and the current size becomes unavailable,
    // prefer Medium when available, otherwise use the first valid option.
    if (!availableCardSizes.includes(cardSize)) {
      const preferredSize = availableCardSizes.includes(
        DEFAULT_PROJECT_CARD_SIZE
      )
        ? DEFAULT_PROJECT_CARD_SIZE
        : availableCardSizes[0];
      setCardSize(preferredSize);
    }
  }, [availableCardSizes, cardSize, setCardSize]);

  return (
    <Box
      display="flex"
      alignItems="center"
      sx={{ display: { xs: 'none', sm: 'flex' } }}
    >
      <ToggleButtonGroup
        value={cardSize}
        exclusive
        onChange={(_, newValue) => {
          if (newValue !== null) setCardSize(newValue as ProjectCardSize);
        }}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            border: `1px solid ${t.borders.subtle}`,
            color: 'text.secondary',
            px: 1,
            py: 0.5,
            '&.Mui-selected': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18),
              },
            },
            '&:hover': {
              bgcolor: t.surfaces.fill,
            },
          },
        }}
      >
        {CARD_SIZE_OPTIONS.filter((option) =>
          availableCardSizes.includes(option.value)
        ).map(({ value, label, Icon }) => (
          <ToggleButton
            key={value}
            value={value}
            aria-label={`${label} project cards`}
          >
            <Tooltip title={label}>
              <Icon sx={{ fontSize: 18 }} />
            </Tooltip>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default CardSizeToggle;
